<template>
    <div v-if="quantityGroups.length > 0" class="mb-4">
        <h3 class="mb-3 d-inline-block">{{ $t('productDetail.quantityDiscount.buyMore') }}</h3>

        <div class="product-badges ml-2 d-inline-block">
            <div class="item quantity-groups-tag">
                <img
                    v-lazy-load
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    data-src="/nuxt-assets/icons/discount.svg"
                    alt=""
                    width="14"
                />
                {{ $t('productDetail.quantityDiscount.tagTitle', { number: lowestAmountFrom }) }}
            </div>
        </div>

        <div class="group">
            <div class="group__imgWrapper">
                <img
                    v-lazy-load
                    class="group__img"
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    :data-src="product.image"
                    :alt="product.name"
                />
            </div>
            <div class="group__lines-wrapper">
                <div v-for="(group, item) in quantityGroups" :key="item">
                    <div v-if="$device.isDesktop" class="group__lines-wrapper__element-wrapper">
                        <div class="group__title">{{ group.title }}</div>

                        <div class="group__price">{{ group.price }} {{ $t('productDetail.priceUnit') }}</div>

                        <div class="group__savebadge">
                            <span v-if="basketAmount >= group.toBasket && group.from === closestHigher">
                                {{
                                    $t('productDetail.quantityDiscount.discountApplying', {
                                        number: Math.round(basketAmount * (group.save / (group.to || group.from)) * 100) / 100,
                                    })
                                }}
                            </span>
                            <span v-else-if="basketAmount < group.toBasket">
                                {{ $t('productDetail.quantityDiscount.discount', { number: Math.round(group.save * 100) / 100 }) }}
                            </span>
                        </div>

                        <a v-if="basketAmount < group.toBasket" class="group__button" @click="changeProductAmount(group.toBasket)">
                            <span class="group__addButton">{{ $t('product.addToCard') }}</span>
                            <span v-if="basketAmount >= group.toBasket && group.from === closestHigher" class="group__saveButton">
                                {{ $t('productDetail.quantityDiscount.discountApplying', { number: Math.round(group.save * 100) / 100 }) }}
                            </span>
                            <span v-else-if="basketAmount < group.toBasket" class="group__saveButton">
                                {{ $t('productDetail.quantityDiscount.discount', { number: Math.round(group.save * 100) / 100 }) }}
                            </span>
                        </a>
                        <div v-else class="group__active">
                            {{ $t('productDetail.quantityDiscount.applied') }}
                        </div>
                    </div>
                    <div v-else class="group__lines-wrapper__element-wrapper">
                        <div>
                            <div class="group__title">{{ group.title }}</div>

                            <div class="group__price">{{ group.price }} {{ $t('productDetail.priceUnit') }}</div>
                        </div>

                        <a v-if="basketAmount < group.toBasket" class="group__button" @click="changeProductAmount(group.toBasket)">
                            <span class="group__addButton">{{ $t('product.addToCard') }}</span>
                            <span v-if="basketAmount >= group.toBasket && group.from === closestHigher" class="group__saveButton">
                                {{ $t('productDetail.quantityDiscount.discountApplying', { number: Math.round(group.save * 100) / 100 }) }}
                            </span>
                            <span v-else-if="basketAmount < group.toBasket" class="group__saveButton">
                                {{ $t('productDetail.quantityDiscount.discount', { number: Math.round(group.save * 100) / 100 }) }}
                            </span>
                        </a>
                        <div v-else class="group__active">
                            {{ $t('productDetail.quantityDiscount.applied') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/javascript" lang="ts">
import {PropType} from "vue";
import { useBasketStore } from '~/stores/basket'
import { addProductToBasket } from '~/utils/addProductToBasket'
import FullProduct from "~/models/Product/FullProduct";

export default {
    props: {
        quantityGroups: {
            type: Array as PropType<{title: string, toBasket: number, from: number, to: number|null, save: number, price: number}[]>,
            required: true,
        },
        product: {
            type: Object as PropType<FullProduct>,
            required: true,
        },
    },

    computed: {
        basketAmount(): number {
            const basket = useBasketStore()
            return basket.productAmounts[this.product.id] || 0
        },

        closestHigher(): number {
            for (let i = this.quantityGroups.length - 1; i >= 0; i--) {
                if (this.basketAmount >= this.quantityGroups[i].from) {
                    return this.quantityGroups[i].from
                }
            }

            return 0
        },

        lowestAmountFrom(): number {
            return Math.min(...this.quantityGroups.map((group: {title: string, toBasket: number, from: number, to: number|null, save: number, price: number}) => group.from))
        },
    },

    methods: {
        changeProductAmount(amount: number): void {
            addProductToBasket({
                fullProduct: this.product,
                productId: this.product.id,
                amount,
                showPreBasket: true,
            })
        },
    },
}
</script>
<style lang="scss" scoped>
.group {
    border-radius: $rounded;
    color: $color-dark;
    background-color: $color-white;
    font-size: 14px;
    line-height: 17px;
    padding: 8px 13px;
    margin-bottom: 1rem;
    box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
    display: flex;

    @media only screen and (min-width: 767px) {
        padding: 25px;
        font-size: 16px;
        line-height: 19px;
    }

    &__active {
        display: flex;
        align-items: center;
        font-size: 14px;
        color: $color-green-dark;
        order: 3;
        font-weight: bold;

        &::before {
            content: '';
            width: 16px;
            height: 16px;
            margin-right: 6px;
            display: block;
            background: $color-grey url('/nuxt-assets/icons/check-circle.svg') no-repeat center center;
            background-size: cover;
            margin-top: 3px;
        }
    }

    &__lines-wrapper {
        width: 100%;
        display: grid;
        row-gap: 20px;

        &__element-wrapper {
            width: 100%;
            display: flex;
            grid-gap: 15px;
            align-items: center;
            justify-content: space-between;

            @media only screen and (min-width: 767px) {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                text-align: center;
            }
        }
    }

    &__title {
        order: 0;
        align-self: center;
        font-weight: bold;
    }

    &__savebadge {
        color: $color-green-dark;
        font-size: 12px;
        background: $color-green-light;
        padding: 5px 25px;
        border-radius: 100px;
        width: auto;
        display: none;

        @media only screen and (min-width: 767px) {
            display: block;
            order: 2;
            align-self: center;
            justify-self: center;
        }
    }

    &__imgWrapper {
        display: none;
        width: 60px;
        height: 60px;
        box-shadow: $box-shadow;
        border-radius: $rounded;
        padding: 5px;
        text-align: center;
        background: rgba(246, 246, 246, 1);
        margin: auto;

        img {
            max-width: 100%;
            max-height: 100%;
        }

        @media only screen and (min-width: 767px) {
            display: block;
        }
    }

    &__price {
        align-self: center;
        order: 1;
        font-weight: bold;
        color: $color-cta;
    }

    &__button {
        flex-grow: 0;
        text-align: center;
        font-size: 14px;
        line-height: 17px;
        border-radius: 100px;
        border: 1px solid $color-cta;
        padding: 5px 20px 5px 20px;
        font-weight: bold;
        cursor: pointer;
        display: inline-block;
        order: 3;

        @media only screen and (min-width: 767px) {
            width: 100%;
            align-self: center;
            justify-self: center;
            max-width: 125px;
        }
    }

    &__addButton {
        display: none;

        @include screen-md() {
            display: block;
        }
    }

    &__saveButton {
        @include screen-md() {
            display: none;
        }
    }
}

.quantity-groups-tag {
    background: rgba(255, 242, 244, 1) !important;
    color: rgba(230, 0, 126, 1) !important;
    font-size: 14px !important;
    font-weight: bold !important;
}
</style>
